/**
 * Data-light + offline mastery + welcome-back utilities.
 * -----------------------------------------------------
 * A single tiny module because these three features share
 * localStorage + online/offline detection plumbing. Keeping
 * them together prevents drift between "how do we know a
 * user is offline" answers.
 */

const KEY_DATA_LIGHT   = "we_data_light";     // "1" | "0"
const KEY_MASTERY_Q    = "we_mastery_offline_queue_v1"; // array of {url, body, ts}
const KEY_LAST_VISIT   = "we_last_visit_ts";
const KEY_LAST_ROUTE   = "we_last_route";
const KEY_SESSION_MARK = "we_session_seen_marker";

/* Data-light ─────────────────────────────────────────────── */

export function isDataLight() {
  try {
    // Honour an explicit user toggle first.
    const flag = localStorage.getItem(KEY_DATA_LIGHT);
    if (flag === "1") return true;
    if (flag === "0") return false;
  } catch { /* ignore */ }
  // Auto-detect via the Network Information API when available.
  try {
    const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!c) return false;
    if (c.saveData === true) return true;
    if (typeof c.effectiveType === "string" && /^(slow-)?2g$/i.test(c.effectiveType)) return true;
  } catch { /* ignore */ }
  return false;
}

export function setDataLight(on) {
  try { localStorage.setItem(KEY_DATA_LIGHT, on ? "1" : "0"); } catch { /* ignore */ }
}

/* Offline mastery queue ──────────────────────────────────── */

function readQueue() {
  try { return JSON.parse(localStorage.getItem(KEY_MASTERY_Q) || "[]"); }
  catch { return []; }
}

function writeQueue(q) {
  try { localStorage.setItem(KEY_MASTERY_Q, JSON.stringify(q.slice(-100))); }
  catch { /* ignore */ }
}

export function enqueueMastery(url, body) {
  const q = readQueue();
  q.push({ url, body, ts: Date.now() });
  writeQueue(q);
}

/**
 * Post the current queue to the backend. Safe to call at any time —
 * a no-op when the queue is empty or navigator says offline. Returns
 * counts so the caller can surface a toast.
 */
export async function flushMasteryQueue() {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return { flushed: 0, remaining: readQueue().length };
  }
  const original = readQueue();
  if (original.length === 0) return { flushed: 0, remaining: 0 };
  const remaining = [];
  let flushed = 0;
  for (const item of original) {
    try {
      const r = await fetch(item.url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(item.body),
      });
      if (r.ok) flushed += 1;
      else remaining.push(item);
    } catch {
      remaining.push(item);
    }
  }
  writeQueue(remaining);
  return { flushed, remaining: remaining.length };
}

/**
 * Fire-and-forget mastery submission wrapped so that a network failure
 * or a 5xx enqueues locally instead of losing the attempt.
 */
export async function submitMasteryProvisional(url, body) {
  try {
    const r = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (r.ok) return { status: "sent", response: await r.json().catch(() => ({})) };
    enqueueMastery(url, body);
    return { status: "queued", reason: `http_${r.status}` };
  } catch {
    enqueueMastery(url, body);
    return { status: "queued", reason: "network" };
  }
}

export function offlineQueueSize() { return readQueue().length; }

/* Welcome-back ───────────────────────────────────────────── */

export function markVisit(pathname) {
  try {
    if (pathname && pathname !== "/") {
      localStorage.setItem(KEY_LAST_ROUTE, pathname);
    }
    // Update on unload only, but seed on first mount so returning
    // users see the difference.
    if (!localStorage.getItem(KEY_SESSION_MARK)) {
      localStorage.setItem(KEY_SESSION_MARK, String(Date.now()));
    }
  } catch { /* ignore */ }
}

export function stampVisit() {
  try {
    localStorage.setItem(KEY_LAST_VISIT, String(Date.now()));
  } catch { /* ignore */ }
}

/**
 * Returns a compact "welcome back" descriptor when the child hasn't
 * been seen for >= 12 hours. Returns null for first-time or same-day
 * visits so no banner ever nags the user.
 */
export function welcomeBackInfo() {
  try {
    const raw = localStorage.getItem(KEY_LAST_VISIT);
    if (!raw) return null;
    const last = Number(raw);
    const now = Date.now();
    const gapMs = now - last;
    // Bail if we've never left or it's been under 12h.
    if (!Number.isFinite(gapMs) || gapMs < 12 * 3600 * 1000) return null;
    const days = Math.floor(gapMs / (24 * 3600 * 1000));
    const hours = Math.floor(gapMs / (3600 * 1000));
    return {
      gapMs,
      humanGap: days >= 1 ? `${days} day${days === 1 ? "" : "s"}` : `${hours} hour${hours === 1 ? "" : "s"}`,
      lastRoute: localStorage.getItem(KEY_LAST_ROUTE) || null,
      queued: offlineQueueSize(),
    };
  } catch { return null; }
}
